import { AuthenticationRoot, SignedInRoute, loadSignedInRoute, SignedOutRoute, loadSignedOutRoute, loadRootRoute, loadStudy, loadDemo } from '@/systems/Authentication'
import { SignedOutLayout } from '@/pages/SignedOutLayout'
import { DashboardLayout, FullpageGenericErrorBoundary, DashboardLayoutErrorBoundary, SplashScreen } from '@/DashboardLayout'
import { LoadingBar } from '@/components/DashboardComponents'
import { NewScenarioLayout } from '@/pages/NewScenarioLayout'
import { scenarioSubmit, loadScenario, loadScenarioForNewAssessment, loadScenarioOverview, loadScenarioPreviews } from '@/data/ScenarioData'
import { NewRubricLayout } from '@/pages/NewRubricLayout'
import { loadRubric, loadRubricOverview, rubricSubmit } from '@/data/RubricData'
import { createBrowserRouter, createRoutesFromElements, Route, Outlet } from "react-router";
import { RouterProvider } from "react-router/dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Filesystem } from '@/pages/Filesystem'
import { loadFiles } from '@/data/FilesystemData'
import { loadSession, sessionSubmit, previewScenario, previewAssessment, startAssessment } from '@/data/SessionData'
import { SessionLayout } from '@/pages/SessionLayout'
import { Courses, ArchivedCourses } from '@/pages/Courses'
import { useParams } from 'react-router'
import { loadCourses, courseAction, loadCourse, joinCourseViaLink, loadCourseMembers, loadCourseSettings } from '@/data/CoursesData'
import { Course, Assessments, People, CourseSettings } from '@/pages/Course'
import { NewAssessmentLayout } from '@/pages/NewAssessmentLayout'
import { loadAssessment, assessmentSubmitCreate, assessmentSubmitUpdate, loadAssessments } from '@/data/AssessmentData'
import { GraderLayoutFrame, ReviewLayoutFrame, GraderLayoutSubmission } from '@/pages/GraderLayout'
import { loadAssessmentGrader, loadAssessmentStudentSubmission, updateSessionFeedback } from '@/data/GraderData'
import { SettingsLayout, Profile, EmailAndSecurity, OrganizationAccess } from '@/pages/SettingsLayout'
import { loadSettings } from '@/data/ProfileData'
import { ModalProvider } from '@/components/Modal'
import { BlockerProvider } from '@/systems/Blocker'
import { MenuProvider } from '@/components/Menu'
import { SyncManagerProvider } from '@/systems/DatabaseSync'
import { VerifyEmailLayout, ResetPasswordLayout } from '@/pages/AuthLinkLayouts'
import { Organizations } from '@/pages/Organizations'
import { Organization, OrganizationDepartmentsAndCourses, OrgPeople, OrgSettings } from '@/pages/Organization'
import { loadOrganization, loadSubOrganizationsAndCourses, loadOrgPeople, loadOrgSettings, loadOrganizations } from '@/data/OrganizationData'
import { JoinStudyLayout } from '@/pages/JoinStudyLayout'
import { DemoLayout } from '@/pages/DemoLayout'
import { ResizeProvider } from '@/hooks/useIsMobile'
import { InmemoryStorageProvider } from '@/hooks/useStorage'
import { ScenarioOverview, ScenarioPreviews } from '@/pages/ScenarioOverview'
import { RubricOverview } from '@/pages/RubricOverview'

const queryClient = new QueryClient();

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="" element={<RouterRoot><Outlet/></RouterRoot>} hydrateFallbackElement={<SplashScreen/>} errorElement={<RouterRoot><FullpageGenericErrorBoundary/></RouterRoot>}>
        <Route path="/" element={<SignedInRoute><LoadingBar/><Outlet /></SignedInRoute>} loader={loadSignedInRoute}>
          <Route path="" id="dashboard_root" element={<DashboardLayout><Outlet /></DashboardLayout>} loader={loadCourses} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed}>
            <Route path="" element={<></>} loader={loadRootRoute} errorElement={<DashboardLayoutErrorBoundary/>}></Route>
            <Route path="scenarios" element={<Filesystem view="scenarios"/>} loader={(params) => loadFiles(params, "scenarios")} errorElement={<DashboardLayoutErrorBoundary title="My scenarios"/>}></Route>
            <Route path="folders/:folder_id" element={<Filesystem/>} loader={(params) => loadFiles(params)} errorElement={<DashboardLayoutErrorBoundary title="Folder"/>}></Route>
            <Route path="scenarios/:scenario_id" element={<ScenarioOverview/>} loader={loadScenarioOverview} errorElement={<DashboardLayoutErrorBoundary title="Scenario"/>}></Route>
            <Route path="scenarios/:scenario_id/previews" element={<ScenarioPreviews/>} loader={loadScenarioPreviews} errorElement={<DashboardLayoutErrorBoundary title="Scenario"/>}></Route>
            <Route path="scenarios/:scenario_id/edit" element={<NewScenarioLayout/>} loader={loadScenario} action={scenarioSubmit} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed}></Route>
            <Route path="scenarios/:scenario_id/preview" element={<></>} loader={previewScenario}></Route>
            <Route path="rubrics" element={<Filesystem view="rubrics"/>} loader={(params) => loadFiles(params, "rubrics")} errorElement={<DashboardLayoutErrorBoundary title="My rubrics"/>}></Route>
            <Route path="rubrics/:rubric_id" element={<RubricOverview/>} loader={loadRubricOverview} errorElement={<DashboardLayoutErrorBoundary title="Rubric"/>}></Route>
            <Route path="rubrics/:rubric_id/edit" element={<NewRubricLayout/>} loader={loadRubric} action={rubricSubmit} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed}></Route>
            <Route path="shared" element={<Filesystem view="shared"/>} loader={(params) => loadFiles(params, "shared")} errorElement={<DashboardLayoutErrorBoundary title="Shared with me"/>}></Route>
            <Route path="trash" element={<Filesystem view="trash"/>} loader={(params) => loadFiles(params, "trash")} errorElement={<DashboardLayoutErrorBoundary title="Trash"/>}></Route>
            <Route path="courses" element={<Courses/>} action={courseAction} errorElement={<DashboardLayoutErrorBoundary title="Courses"/>}></Route>
            <Route path="archived-courses" element={<ArchivedCourses/>} errorElement={<DashboardLayoutErrorBoundary title="Archived courses"/>}></Route>
            <Route path="organizations?/:organization_id?/courses/:course_id" id="course" element={<Course/>} loader={loadCourse} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<DashboardLayoutErrorBoundary title="Course"/>}>
              <Route path="" id="assessments" element={<Assessments/>} loader={loadAssessments}></Route>
              <Route path="people" element={<People/>} loader={loadCourseMembers}></Route>
              <Route path="settings" id="course_settings" element={<CourseSettings/>} loader={loadCourseSettings}></Route>
              <Route path="assessments/:assessment_id/edit" element={<NewAssessmentLayout/>} loader={loadAssessment} action={assessmentSubmitUpdate} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              <Route path="assessments/:assessment_id/edit-scenario" element={<NewScenarioLayout/>} loader={loadScenario} action={scenarioSubmit} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              <Route path="assessments/new/:scenario_id" element={<NewAssessmentLayout is_new={true}/>} action={assessmentSubmitCreate} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} loader={loadScenarioForNewAssessment} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              <Route path="assessments/:assessment_id/preview" element={<></>} loader={previewAssessment} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              <Route path="assessments/:assessment_id/start" element={<></>} loader={startAssessment} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              <Route path="assessments/:assessment_id/grader" id="grader" element={<GraderLayoutFrame/>} loader={loadAssessmentGrader} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<FullpageGenericErrorBoundary/>}>
                <Route path=":student_uid" element={<GraderLayoutSubmission/>} loader={loadAssessmentStudentSubmission} action={updateSessionFeedback} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              </Route>
              <Route path="assessments/:assessment_id/review" element={<ReviewLayoutFrame from="assessment"/>} loader={loadAssessmentStudentSubmission} errorElement={<FullpageGenericErrorBoundary/>} />
            </Route>
            <Route path="organizations" element={<Organizations/>} errorElement={<DashboardLayoutErrorBoundary title="Organizations"/>} loader={loadOrganizations}></Route>
            <Route path="organizations/:organization_id" id="organization" element={<Organization/>} loader={loadOrganization} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<DashboardLayoutErrorBoundary title="Organization"/>}>
              <Route path="" id="suborganizations" element={<OrganizationDepartmentsAndCourses/>} loader={loadSubOrganizationsAndCourses}/>
              <Route path="people" element={<OrgPeople/>} loader={loadOrgPeople}></Route>
              <Route path="settings" id="orgsettings" element={<OrgSettings/>} loader={loadOrgSettings}></Route>
            </Route>
            <Route path="session/:session_id" element={<SessionLayoutWrapper/>} loader={loadSession} action={sessionSubmit} shouldRevalidate={revalidateUnlessActionWasPerformed}></Route>
            <Route path="session/:session_id/review" element={<ReviewLayoutFrame from="session"/>} loader={loadAssessmentStudentSubmission} action={updateSessionFeedback} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<FullpageGenericErrorBoundary/>} />
            <Route path="join/:join_code/:assessment_id?" element={<></>} loader={joinCourseViaLink}></Route>
            <Route path="settings" element={<SettingsLayout/>} loader={loadSettings}>
              <Route path="profile" element={<Profile/>}/>
              <Route path="security" element={<EmailAndSecurity/>}/>
              <Route path="organizations" element={<OrganizationAccess/>}/>
            </Route>
          </Route>
        </Route>
        <Route path="/login" element={<SignedOutRoute><SignedOutLayout /></SignedOutRoute>} loader={loadSignedOutRoute}></Route>
        <Route path="/study/:join_code" element={<JoinStudyLayout/>} loader={loadStudy} errorElement={<FullpageGenericErrorBoundary/>}></Route>
        <Route path="/demo/:join_code/:assessment_id" element={<DemoLayout/>} loader={loadDemo} errorElement={<FullpageGenericErrorBoundary/>}></Route>
        {/* <Route path="/waitlist" element={<WaitlistLayout/>}></Route> */}
        <Route path="/verify/:oobCode" element={<VerifyEmailLayout/>}></Route>
        <Route path="/reset/:oobCode" element={<ResetPasswordLayout/>}></Route>
      </Route>
    </>
  )
)

function SessionLayoutWrapper() {
  const { session_id } = useParams();
  return <SessionLayout key={session_id} />;
}

function revalidateUnlessActionFailed({actionResult}) {
  const failed = actionResult?.success === false;

  return !failed;
}

function defaultExceptDontRevalidateIfActionFailed({actionResult, defaultShouldRevalidate, currentUrl, nextUrl}) {
  const failed = actionResult?.success === false;
  const dont_revalidate = actionResult?.dont_revalidate === true;

  if (failed || dont_revalidate) {
    return false;
  } else {

    if (currentUrl?.search === "" && nextUrl?.search.includes("?folder_id=")) {
      return false;
    }

    return defaultShouldRevalidate;
  }
}

function revalidateUnlessActionWasPerformed({actionResult}) {
  return !actionResult;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <InmemoryStorageProvider>
        <AuthenticationRoot>
          <SyncManagerProvider>
            <RouterProvider router={router} />
          </SyncManagerProvider>
        </AuthenticationRoot>
      </InmemoryStorageProvider>
    </QueryClientProvider>
  )
}

function RouterRoot({ children }) {
  return (
    <BlockerProvider>
      <ResizeProvider>
        <MenuProvider>
          <ModalProvider>
            {children}
          </ModalProvider>
        </MenuProvider>
      </ResizeProvider>
    </BlockerProvider>
  )
}